import { Box, Button, Grow, Popper, Slide, Stack } from "@mui/material";
import { ArrowRightIcon } from "@mui/x-date-pickers";
import { usePopover } from "../../utils/hooks/use-popover";
import { useAccountChannels } from "./hooks/use-account-channels";
import { useLaunchComms } from "./hooks/use-launch-comms";

import type { ContentTone } from "../brand-kit/server/brand-kit-service";
import type { BoxProps } from "@mui/material";
import type { FC } from "react";

interface GenerateCommsButtonProps extends BoxProps {
  contentTone: ContentTone | "custom";
  description: string;
}

export const GenerateCommsButton: FC<GenerateCommsButtonProps> = ({ contentTone, description }) => {
  const [anchorRef, createButtonPopover] = usePopover<HTMLButtonElement>();

  const { channels, loading: channelsLoading } = useAccountChannels();
  const { launch, launching, savable } = useLaunchComms(contentTone, description);

  if (channelsLoading) return null;

  return (
    <Box onMouseOut={createButtonPopover.handleClose} sx={{ position: "relative" }}>
      <Popper
        onMouseOut={createButtonPopover.handleClose}
        onMouseOver={createButtonPopover.handleOpen}
        open={!launching && createButtonPopover.open}
        // oxlint-disable-next-line react/refs -- MUI anchorEl needs the DOM node; the popover only opens after the anchor has mounted
        anchorEl={anchorRef.current}
        placement="top-start"
        sx={{ zIndex: 2000 }}
        transition
      >
        {({ TransitionProps }) => (
          <Box
            sx={{
              width: anchorRef.current?.offsetWidth,
              display: "flex",
              flexDirection: "row-reverse",
              borderRadius: "10px 10px 0 0",
              bgcolor: "primary.light",
              p: 2,
              overflow: "hidden",
              zIndex: 3000,
            }}
          >
            <Slide direction="up" in={createButtonPopover.open} mountOnEnter unmountOnExit>
              <Stack direction="column" spacing={1} sx={{ width: "100%" }}>
                {channels.map(({ name, channel_name, icon }, i) => (
                  <Grow {...TransitionProps} timeout={100 * i} key={channel_name}>
                    <Button
                      data-analytics-id={`generate-moment-create-your-moment-${channel_name}`}
                      startIcon={icon}
                      variant="outlined"
                      fullWidth
                      disabled={!savable}
                      sx={{
                        backgroundColor: "#fff",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.8)",
                        },
                      }}
                      onClick={() => savable && void launch(channel_name).catch(console.error)}
                    >
                      {name}
                    </Button>
                  </Grow>
                ))}
              </Stack>
            </Slide>
          </Box>
        )}
      </Popper>
      <Button
        data-analytics-id="generate-moment-create-your-moment"
        loading={launching || !savable}
        endIcon={
          <ArrowRightIcon
            sx={{
              transition: "transform 0.2s ease-in-out",
              ...(createButtonPopover.open && {
                transform: "rotate(-90deg)",
              }),
            }}
          />
        }
        variant="contained"
        sx={(theme) => ({
          position: "relative",
          zIndex: 2,
          cursor: "default",
          ...(createButtonPopover.open && {
            borderTopLeftRadius: "0px",
            borderTopRightRadius: "0px",
            bgcolor: `${theme.palette.primary.light} !important`,
          }),
          transition: "transform 0.2s ease-in-out",
        })}
        onMouseOver={createButtonPopover.handleOpen}
        ref={anchorRef}
      >
        Edit this Communication
      </Button>
    </Box>
  );
};
